import { useController } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import Select from 'app/frontend/base/components/Select'
import FileUploadQuestion from './FileUploadQuestion'
import { cn } from '@/lib/utils'
import { Undo2, XIcon } from 'lucide-react'

type ExistingAttachment = wowaudit.ApplicationAnswerAttachment & {
  _destroy?: boolean
}

type Props = {
  question: wowaudit.Question
  fileAttachments: File[]
  onFileAttachmentsChange: (files: File[]) => void
  existingAttachments?: ExistingAttachment[]
  onExistingAttachmentDelete?: (attachmentId: number) => void
} & React.HTMLAttributes<HTMLDivElement>

const ApplicationQuestion = ({
  question,
  fileAttachments,
  onFileAttachmentsChange,
  existingAttachments = [],
  onExistingAttachmentDelete,
  className,
  style,
}: Props) => {
  const { field } = useController({ name: `answers.${question.id}` })

  if (question.questionType === 'textarea') {
    return (
      <Textarea
        placeholder='Your answer'
        className={cn('min-h-[100px]', className)}
        style={style}
        {...field}
      />
    )
  }

  if (question.questionType === 'number') {
    return (
      <Input
        type='number'
        placeholder='Your answer'
        {...field}
        className={className}
        style={style}
      />
    )
  }

  if (question.questionType === 'radio') {
    const options =
      question.questionOptions?.filter(
        (opt): opt is typeof opt & { option: string } => Boolean(opt.option),
      ) ?? []

    return (
      <RadioGroup
        value={field.value}
        onValueChange={(value) => field.onChange(value)}
        className={cn(
          'flex flex-col gap-2 pb-4',
          className,
          'bg-transparent border-none',
        )}
        style={style}
      >
        {options.map((opt) => (
          <div key={opt.option} className='flex items-center gap-2'>
            <RadioGroupItem
              value={opt.option}
              id={`${question.id}-${opt.option}`}
            />
            <Label
              htmlFor={`${question.id}-${opt.option}`}
              className='cursor-pointer'
            >
              {opt.option}
            </Label>
          </div>
        ))}
      </RadioGroup>
    )
  }

  if (question.questionType === 'dropdown') {
    return (
      <Select
        options={
          question.questionOptions
            ?.filter((opt): opt is typeof opt & { option: string } =>
              Boolean(opt.option),
            )
            .map((opt) => ({
              label: opt.option,
              value: opt.option,
              sortLabel: '',
            })) ?? []
        }
        value={field.value}
        setValue={(value) => field.onChange(value ?? '')}
        placeholder='Select an option'
        className={className}
        style={style}
      />
    )
  }

  if (question.questionType === 'checkbox') {
    const options =
      question.questionOptions?.filter(
        (opt): opt is typeof opt & { option: string } => Boolean(opt.option),
      ) ?? []

    if (options.length > 0) {
      const selected = field.value ? field.value.split(', ') : []

      return (
        <div
          className={cn(
            'flex flex-col gap-2 pb-4',
            className,
            'bg-transparent border-none',
          )}
          style={style}
        >
          {options.map((opt) => (
            <div key={opt.option} className='flex items-center gap-2'>
              <Checkbox
                id={`${question.id}-${opt.option}`}
                checked={selected.includes(opt.option)}
                onCheckedChange={(checked) =>
                  field.onChange(
                    options
                      .filter((o) =>
                        o.option === opt.option
                          ? checked
                          : selected.includes(o.option),
                      )
                      .map((o) => o.option)
                      .join(', '),
                  )
                }
              />
              <Label
                htmlFor={`${question.id}-${opt.option}`}
                className='cursor-pointer'
              >
                {opt.option}
              </Label>
            </div>
          ))}
        </div>
      )
    }

    return (
      <label className='flex items-center gap-2 cursor-pointer my-6'>
        <Checkbox
          checked={field.value === 'true'}
          onCheckedChange={(checked) => field.onChange(checked ? 'true' : '')}
        />
        <span>
          {question.description}
          {question.required && (
            <span className='text-destructive-foreground ml-1'>*</span>
          )}
        </span>
      </label>
    )
  }

  if (question.questionType === 'file') {
    return (
      <div className='space-y-2'>
        {existingAttachments.length > 0 && (
          <div className='flex flex-col gap-2'>
            {existingAttachments.map((attachment) => (
              <div
                key={attachment.id}
                className={cn(
                  'group relative flex items-center gap-3 rounded-md border px-3 py-2 border-primary/50',
                  attachment._destroy ? 'opacity-40' : '',
                  className,
                )}
                style={style}
              >
                <img
                  src={attachment.url}
                  alt={attachment.originalFilename}
                  className='h-10 w-10 shrink-0 rounded object-cover'
                />
                <span className='min-w-0 flex-1 truncate text-sm'>
                  {attachment.originalFilename}
                </span>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='h-7 w-7 shrink-0'
                  onClick={() => onExistingAttachmentDelete?.(attachment.id)}
                >
                  {attachment._destroy ? (
                    <Undo2 className='h-4 w-4' />
                  ) : (
                    <XIcon className='h-4 w-4' />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
        <FileUploadQuestion
          value={fileAttachments}
          onChange={onFileAttachmentsChange}
          previousLength={existingAttachments.length}
          maxFiles={5}
          className={className}
          style={style}
        />
      </div>
    )
  }

  return <Input placeholder='Your answer' {...field} />
}

export default ApplicationQuestion
